import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { ADMIN_ACCESS_ROLES } from '../constants'

const AdminContext = createContext()

/** Returns true only when both the sessionStorage flag is set AND the
 *  localStorage session contains a role that legitimately allows admin access.
 *  This prevents someone from simply setting the flag via DevTools. */
function isValidAdminSession() {
  try {
    if (sessionStorage.getItem('adminAuthenticated') !== 'true') return false
    const session = JSON.parse(localStorage.getItem('session') || 'null')
    if (!session) return false
    if (session.uid === 'system-admin' || session.mode === 'system-admin') return true
    const roles = Array.isArray(session.roles)
      ? session.roles
      : session.role ? [session.role] : []
    return roles.some(r => ADMIN_ACCESS_ROLES.includes(String(r).toLowerCase()))
  } catch {
    return false
  }
}

export const AdminProvider = ({ children }) => {
  const [isAdminMode, setIsAdminMode] = useState(() => isValidAdminSession())

  const syncState = useCallback(() => {
    setIsAdminMode(isValidAdminSession())
  }, [])

  useEffect(() => {
    // Cross-tab: storage events fire when another tab changes localStorage/sessionStorage
    window.addEventListener('storage', syncState)
    // Same-tab: custom event dispatched by toggleAdminMode and login/logout flows
    window.addEventListener('adminModeChanged', syncState)
    return () => {
      window.removeEventListener('storage', syncState)
      window.removeEventListener('adminModeChanged', syncState)
    }
  }, [syncState])

  const toggleAdminMode = () => {
    const newValue = !isAdminMode
    if (newValue) {
      // Only allow enabling if the session actually has admin roles
      if (!isValidAdminSession()) return
      sessionStorage.setItem('adminAuthenticated', 'true')
    } else {
      sessionStorage.removeItem('adminAuthenticated')
      sessionStorage.setItem('justExitedAdminMode', 'true')
    }
    setIsAdminMode(newValue)
    window.dispatchEvent(new CustomEvent('adminModeChanged', { detail: { isAdmin: newValue } }))
  }

  return (
    <AdminContext.Provider value={{ isAdminMode, toggleAdminMode }}>
      {children}
    </AdminContext.Provider>
  )
}

export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}
